import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, LogOut, Store, Search, Heart, MessageCircle, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import api from "@/lib/api";
import altezaLogo from "@/assets/alteza-logo.png";

const CATEGORIES = [
  { label: "Shop all", to: "/shop" },
  { label: "New Arrivals", to: "/#new-arrivals" },
  { label: "Best Sellers", to: "/#best-sellers" },
  { label: "Categories", to: "/#categories" },
  { label: "Browse shops", to: "/shops" },
  { label: "About", to: "/#how-it-works" },
  { label: "Help Centre", to: "/help" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!user || user === false) { setUnread(0); return; }
    const fetchUnread = () =>
      api.get("/messages/unread_count").then(({ data }) => setUnread(data.count)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  // Close drawer on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const onSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/shop?search=${encodeURIComponent(term)}` : "/shop");
    setMenuOpen(false);
  };

  const isAuthed = user && user !== false;

  return (
    <>
      <header className="backdrop-blur-xl bg-background/80 border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 lg:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <img src={altezaLogo} alt="Alteza Marketplace" className="h-9 lg:h-11 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <Link to="/shop" data-testid="nav-shop" className="hover:text-primary transition-colors">Shop</Link>
            <Link to="/#new-arrivals" data-testid="nav-new-arrivals" className="hover:text-primary transition-colors">New Arrivals</Link>
            <Link to="/#best-sellers" data-testid="nav-best-sellers" className="hover:text-primary transition-colors">Best Sellers</Link>
            <Link to="/#categories" data-testid="nav-categories" className="hover:text-primary transition-colors">Categories</Link>
            <Link to="/shops" data-testid="nav-shops" className="hover:text-primary transition-colors">Shops</Link>
            <Link to="/#how-it-works" data-testid="nav-about" className="hover:text-primary transition-colors">About</Link>
            <Link to="/help" data-testid="nav-help" className="hover:text-primary transition-colors">Help</Link>
            <Link to="/sell" data-testid="nav-sell" className="text-muted-foreground hover:text-primary transition-colors text-[13px] whitespace-nowrap">Sell on Alteza</Link>
          </nav>

          {/* Desktop search */}
          <form onSubmit={onSearch} className="hidden xl:flex items-center gap-2 flex-1 max-w-[180px] border-b border-border focus-within:border-primary transition-colors">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Alteza…"
              className="bg-transparent outline-none text-sm py-2 w-full placeholder:text-muted-foreground"
            />
          </form>

          {/* Right side icons */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link to="/wishlist" data-testid="wishlist-link" className="relative p-2 hover:text-primary transition-colors" aria-label="Wishlist">
              <Heart className="w-5 h-5" />
              {wishCount > 0 && (
                <span data-testid="wishlist-count" className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">
                  {wishCount}
                </span>
              )}
            </Link>
            <Link to="/cart" data-testid="cart-link" className="relative p-2 hover:text-primary transition-colors">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">
                  {count}
                </span>
              )}
            </Link>
            {isAuthed && (
              <Link to="/messages" data-testid="messages-link" className="relative p-2 hover:text-primary transition-colors" aria-label="Messages">
                <MessageCircle className="w-5 h-5" />
                {unread > 0 && (
                  <span data-testid="unread-count" className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-semibold">
                    {unread}
                  </span>
                )}
              </Link>
            )}

            {/* Mobile-only: My Shop quick icon for sellers/admins */}
            {isAuthed && (user.role === "seller" || user.role === "admin") && (
              <Link
                to="/seller"
                data-testid="mobile-myshop-icon"
                className="lg:hidden p-2 hover:text-primary transition-colors"
                aria-label="My Shop"
              >
                <Store className="w-5 h-5" />
              </Link>
            )}

            {/* Desktop: account + role links */}
            {isAuthed ? (
              <div className="hidden lg:flex items-center gap-2">
                {(user.role === "seller" || user.role === "admin") && (
                  <Link to="/seller" data-testid="seller-dashboard-link" className="flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors px-2">
                    <Store className="w-4 h-4" /> My Shop
                  </Link>
                )}
                {user.role === "admin" && (
                  <>
                    <Link to="/admin/sellers" data-testid="admin-sellers-link" className="text-sm font-medium hover:text-primary transition-colors px-2">Sellers</Link>
                    <Link to="/admin/orders" data-testid="admin-orders-link" className="text-sm font-medium hover:text-primary transition-colors px-2">Orders</Link>
                    <Link to="/admin/collections" data-testid="admin-collections-link" className="text-sm font-medium hover:text-primary transition-colors px-2">Editorial</Link>
                    <Link to="/admin/resources" data-testid="admin-resources-link" className="text-sm font-medium hover:text-primary transition-colors px-2">Resources</Link>
                    <Link to="/admin/subscribers" data-testid="admin-subscribers-link" className="text-sm font-medium hover:text-primary transition-colors px-2">Subscribers</Link>
                  </>
                )}
                <Link to="/account" data-testid="account-link" className="flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors px-2">
                  <User className="w-4 h-4" /> {user.name?.split(" ")[0]}
                </Link>
                <button data-testid="logout-btn" onClick={logout} className="p-2 hover:text-primary transition-colors" aria-label="Logout">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                state={{ from: location.pathname }}
                data-testid="login-link"
                className="hidden lg:inline-block btn-outline text-sm px-5 py-2 ml-2"
              >
                Sign in
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden p-2 hover:text-primary transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu-drawer">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm fade-in" onClick={() => setMenuOpen(false)} />
          <aside className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-background border-l border-border flex flex-col shadow-2xl overflow-y-auto" style={{ animation: "slideIn 0.3s cubic-bezier(0.2,0.7,0.2,1) both" }}>
            <div className="flex items-center justify-between px-6 h-16 border-b border-border">
              <img src={altezaLogo} alt="Alteza Marketplace" className="h-8 w-auto object-contain" />
              <button onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2 hover:text-primary"><X className="w-5 h-5" /></button>
            </div>

            {/* Mobile search */}
            <form onSubmit={onSearch} className="px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2 border border-border rounded-sm px-3 focus-within:border-primary transition-colors">
                <Search className="w-4 h-4 text-muted-foreground" />
                <input
                  data-testid="mobile-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search Alteza…"
                  className="bg-transparent outline-none text-sm py-2.5 w-full placeholder:text-muted-foreground"
                />
              </div>
            </form>

            {/* Account block (top) */}
            {isAuthed ? (
              <div className="px-6 py-5 border-b border-border bg-secondary/40">
                <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-semibold mb-1">Signed in as</div>
                <div className="font-serif text-xl">{user.business_name || user.name}</div>
                <div className="text-xs text-muted-foreground italic">{user.email}</div>
              </div>
            ) : (
              <div className="px-6 py-5 border-b border-border bg-secondary/40 flex gap-3">
                <Link to="/login" data-testid="mobile-login" className="btn-outline flex-1 py-3 text-sm rounded-sm text-center">Sign in</Link>
                <Link to="/register" data-testid="mobile-register" className="btn-primary flex-1 py-3 text-sm rounded-sm text-center">Join</Link>
              </div>
            )}

            {/* Account-specific shortcuts */}
            {isAuthed && (
              <div className="px-6 py-2 border-b border-border">
                {(user.role === "seller" || user.role === "admin") && (
                  <>
                    <Link to="/seller" data-testid="mobile-myshop-link" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary">
                      <span className="flex items-center gap-3"><Store className="w-4 h-4" /> My Shop</span>
                      <span className="text-muted-foreground">→</span>
                    </Link>
                    <Link to="/seller?new=1" data-testid="mobile-add-product" className="flex items-center justify-between py-3 text-sm hover:text-primary border-t border-border/60 pl-8 text-muted-foreground">
                      <span className="flex items-center gap-2">+ Add a new piece</span>
                      <span>→</span>
                    </Link>
                  </>
                )}
                {user.role === "admin" && (
                  <>
                    <Link to="/admin/sellers" data-testid="mobile-admin-sellers" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                      <span>Sellers directory</span><span className="text-muted-foreground">→</span>
                    </Link>
                    <Link to="/admin/orders" data-testid="mobile-admin-orders" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                      <span>Orders</span><span className="text-muted-foreground">→</span>
                    </Link>
                    <Link to="/admin/collections" data-testid="mobile-admin-editorial" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                      <span>Editorial</span><span className="text-muted-foreground">→</span>
                    </Link>
                    <Link to="/admin/resources" data-testid="mobile-admin-resources" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                      <span>Seller Resources</span><span className="text-muted-foreground">→</span>
                    </Link>
                    <Link to="/admin/subscribers" data-testid="mobile-admin-subscribers" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                      <span>Subscribers</span><span className="text-muted-foreground">→</span>
                    </Link>
                  </>
                )}
                <Link to="/account" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                  <span className="flex items-center gap-3"><User className="w-4 h-4" /> Account &amp; orders</span>
                  <span className="text-muted-foreground">→</span>
                </Link>
                <Link to="/messages" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                  <span className="flex items-center gap-3"><MessageCircle className="w-4 h-4" /> Messages</span>
                  {unread > 0 && <span className="bg-primary text-primary-foreground text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">{unread}</span>}
                </Link>
                <Link to="/wishlist" className="flex items-center justify-between py-3.5 text-base font-medium hover:text-primary border-t border-border/60">
                  <span className="flex items-center gap-3"><Heart className="w-4 h-4" /> Wishlist</span>
                  {wishCount > 0 && <span className="text-xs text-muted-foreground">{wishCount}</span>}
                </Link>
              </div>
            )}

            {/* Shop categories */}
            <div className="px-6 py-4">
              <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-semibold mb-2">Browse</div>
              {CATEGORIES.map((c) => (
                <Link key={c.to} to={c.to} onClick={() => setMenuOpen(false)} className="block py-3 text-base hover:text-primary border-b border-border/60 last:border-b-0">
                  {c.label}
                </Link>
              ))}
            </div>

            {/* Become a seller */}
            <div className="px-6 py-4 border-t border-border">
              <Link to="/sell" onClick={() => setMenuOpen(false)} data-testid="mobile-sell-link" className="block btn-outline text-center py-3.5 text-sm rounded-sm">
                Sell on Alteza
              </Link>
            </div>

            {/* Sign out */}
            {isAuthed && (
              <div className="px-6 py-4 border-t border-border mt-auto">
                <button data-testid="mobile-logout" onClick={() => { logout(); setMenuOpen(false); }} className="w-full text-left text-sm text-muted-foreground hover:text-destructive py-2 flex items-center gap-2">
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            )}
          </aside>
          <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        </div>
      )}
    </>
  );
}
